import { useRef, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/Skeleton";

/**
 * Auth + lifecycle gate.
 *  1. While AuthContext is bootstrapping → full-screen skeleton
 *  2. No session → redirect to /login (preserving from-location)
 *  3. profile.status drives onboarding / suspension redirects.
 *     null status is treated as 'approved' (legacy rows).
 *
 *  Fix: We track whether we have EVER had a session. If loading
 *  briefly flips back to true after initial boot (onAuthStateChange),
 *  we keep rendering children instead of showing a black screen.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { session, profile, loading } = useAuth();
  const location = useLocation();
  const path = location.pathname;

  // Track if we have ever successfully loaded a session
  const everHadSession = useRef(false);
  if (session) everHadSession.current = true;

  // Only show skeleton on INITIAL load (never had a session yet)
  if (loading && !everHadSession.current) {
    return (
      <div className="min-h-screen p-8 bg-black">
        <Skeleton className="h-12 w-64 mb-6" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // If loading but we previously had a session, keep rendering children
  // This prevents black screens during re-auth / token refresh
  if (loading && everHadSession.current) {
    return <>{children}</>;
  }

  if (!session) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // ---- Lifecycle status checks (only fire when status is set) -------
  const status = profile?.status ?? null;

  if (status === "pending" && !path.startsWith("/onboarding")) {
    return <Navigate to="/onboarding" replace />;
  }
  if (status === "pending_approval" && !path.startsWith("/onboarding")) {
    return <Navigate to="/onboarding/pending" replace />;
  }
  if (status === "rejected" && path !== "/onboarding/rejected") {
    return <Navigate to="/onboarding/rejected" replace />;
  }
  if (status === "suspended" && path !== "/suspended") {
    return <Navigate to="/suspended" replace />;
  }

  // null / 'approved' → render normally
  return <>{children}</>;
}
